import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, signOut } from 'firebase/auth';
import { getStorage, FirebaseStorage, ref, getDownloadURL, uploadString } from 'firebase/storage';
import { getAnalytics, Analytics } from 'firebase/analytics';
import firebaseConfig from '@/config/firebase';
import { ACCESS_TOKEN } from '@/constants';
import { removeFromStorage } from './storage';

class FirebaseService {
    private static instance: FirebaseService;
    private app: FirebaseApp;
    private auth: Auth;
    private storage: FirebaseStorage;
    private analytics: Analytics;

    private constructor() {
        this.app = initializeApp(firebaseConfig);
        this.auth = getAuth(this.app);
        this.storage = getStorage(this.app);
        this.analytics = getAnalytics(this.app);
    }

    public static getInstance(): FirebaseService {
        if (!FirebaseService.instance) {
            FirebaseService.instance = new FirebaseService();
        }
        return FirebaseService.instance;
    }

    public getAuth() {
        return this.auth;
    }

    public getAuthToken() {
        const { currentUser } = this.auth;
        return currentUser ? currentUser.getIdToken(true) : null;
    }

    public async logOut() {
        await signOut(this.auth);
        await removeFromStorage(ACCESS_TOKEN);
    }

    public getAnalytics() {
        return this.analytics;
    }

    public getStorage() {
        return this.storage;
    }

    public async getFileUrl(path: string) {
        if (path.length > 0) {
            const fileRef = ref(this.storage, path);
            return await getDownloadURL(fileRef);
        }
    }

    private getMimeType(file: string): string | null {
        const match = file.match(/^data:([^;]+);base64,/);
        return match ? match[1] : null;
    }

    private getFilePath(filePath: string, fileName: string): string {
        return `contents/${filePath}/${fileName}`;
    }

    public async uploadFile(fileString: string, filePath: string, fileName: string) {
        const mimeType = this.getMimeType(fileString);
        const path = this.getFilePath(filePath, fileName);
        if (!mimeType) {
            throw new Error('Unable to determine MIME type for the file.');
        }
        const storageRef = ref(this.storage, path);
        await uploadString(storageRef, fileString, 'data_url', { contentType: mimeType });
        return { contentType: mimeType, path };
    }
}

export default FirebaseService;
export const firebaseService = FirebaseService.getInstance();
